// libs
import moment from 'moment'
import React from 'react'

import { EventFormat, MeetingFragmentFragment, useCreateMeetingMutation, useUpdateMeetingMutation } from '@lopo-tech/lopo-api'

// components
import AddressPicker from 'src/components/address_picker/address_picker'
import InputArea from 'src/components/forms/input_area/input_area'
import InputDatePicker from 'src/components/forms/input_date_picker/input_date_picker'
import InputField from 'src/components/forms/input_field/input_field'
import SelectField from 'src/components/forms/select-field/select-field'
import TimePicker from 'src/components/forms/time_picker/time_picker'
import Modal from 'src/components/modal/modal'

// constants
import { CLASS_PREFIX } from 'src/constants'

import { IAddress } from 'src/interfaces/address'

import DateHelper from 'src/utils/date_helper/date'

// styles
import { textResources } from 'src/lang/de'
import '../project_modal.scss'
import './project_create_meeting_form.scss'

type Props = {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;

  /** If provided, the modal will prefill and update instead of create. */
  prefill?: MeetingFragmentFragment;
};

const cls = `${CLASS_PREFIX}project-modal`
const formCls = `${CLASS_PREFIX}project-create-meeting-form`
const t = textResources.projects

const FORMAT_OPTIONS = [
  {
    value: EventFormat.Online,
    text: t.createMeetingForm.meetingFormatOnline
  },
  {
    value: EventFormat.Hybrid,
    text: t.createMeetingForm.meetingFormatHybrid
  },
  {
    value: EventFormat.InPerson,
    text: t.createMeetingForm.meetingFormatInPerson
  }
]

export const ProjectCreateMeetingModal: React.FC<Props> = ({
  projectId,
  onClose,
  onCreated,
  prefill
}) => {
  const [createMeeting] = useCreateMeetingMutation()
  const [updateMeeting] = useUpdateMeetingMutation()

  const isEditMode = !!prefill?.id
  const prefilledAddress = prefill?.address ?? undefined

  const initialStartMoment = React.useMemo(
    () => (prefill?.startTime ? moment(prefill.startTime) : null),
    [prefill?.startTime]
  )
  const initialEndMoment = React.useMemo(
    () => (prefill?.endTime ? moment(prefill.endTime) : null),
    [prefill?.endTime]
  )

  const initialDate = initialStartMoment?.isValid()
    ? initialStartMoment.format('YYYY-MM-DD')
    : ''
  const initialFromTime = initialStartMoment?.isValid()
    ? initialStartMoment.format('HH:mm')
    : ''
  const initialToTime = initialEndMoment?.isValid()
    ? initialEndMoment.format('HH:mm')
    : ''

  const [title, setTitle] = React.useState(prefill?.title ?? '')
  const [date, setDate] = React.useState(initialDate)
  const [fromTime, setFromTime] = React.useState(initialFromTime)
  const [toTime, setToTime] = React.useState(initialToTime)
  const [format, setFormat] = React.useState<EventFormat>(prefill?.format ?? EventFormat.InPerson)
  const [locationDescription, setLocationDescription] = React.useState(
    prefill?.locationDescription ?? ''
  )
  const [placeId, setPlaceId] = React.useState(
    prefill?.address?.googlePlaceId ?? ''
  )
  const [onlineMeetingUrl, setOnlineMeetingUrl] = React.useState(
    prefill?.onlineMeetingUrl ?? ''
  )
  const [description, setDescription] = React.useState(prefill?.description ?? '')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const hasLocation = React.useMemo(() => !!placeId, [placeId])

  const hasOnlineUrl = React.useMemo(
    () => onlineMeetingUrl.trim().length > 0,
    [onlineMeetingUrl]
  )

  const showOnlineMeetingUrl =
    format === EventFormat.Online || format === EventFormat.Hybrid
  const showPhysicalLocation =
    format === EventFormat.InPerson || format === EventFormat.Hybrid

  const onAddressChange = React.useCallback(
    (address?: IAddress) => {
      setPlaceId(address?.googlePlaceId ?? '')
    },
    [setPlaceId]
  )

  const onFormatSelect = React.useCallback(
    (value: string) => {
      setFormat(value as EventFormat)
    },
    [setFormat]
  )

  const parseDateTime = React.useCallback(
    (dateString: string, timeString: string) => {
      return moment(`${dateString} ${timeString}`, 'YYYY-MM-DD HH:mm', true)
    },
    []
  )

  const isDateNotInPast = React.useMemo(() => {
    if (!date.trim()) {
      return false
    }

    return DateHelper.isTodayOrBefore(date.trim())
  }, [date])

  const isStartTimeNotInPast = React.useMemo(() => {
    if (!date.trim() || !fromTime.trim()) {
      return false
    }

    if (!DateHelper.isToday(date.trim())) {
      return true
    }

    return DateHelper.isAfterNow(date.trim(), fromTime.trim())
  }, [date, fromTime])

  const isTimeValid = React.useMemo(() => {
    if (!date || !fromTime.trim() || !toTime.trim()) {
      return false
    }

    const startMoment = parseDateTime(date, fromTime.trim())
    const endMoment = parseDateTime(date, toTime.trim())

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return false
    }

    return startMoment.isBefore(endMoment)
  }, [date, fromTime, toTime, parseDateTime])

  const formatRequirementsMet = React.useMemo(() => {
    if (format === EventFormat.Online) {
      return hasOnlineUrl
    }
    if (format === EventFormat.Hybrid) {
      return hasOnlineUrl && hasLocation
    }
    return hasLocation
  }, [format, hasLocation, hasOnlineUrl])

  const canSubmit = React.useMemo(() => {
    return (
      !isSubmitting &&
      title.trim().length > 0 &&
      date.trim().length > 0 &&
      fromTime.trim().length > 0 &&
      toTime.trim().length > 0 &&
      isDateNotInPast &&
      isStartTimeNotInPast &&
      isTimeValid &&
      formatRequirementsMet
    )
  }, [
    title,
    date,
    fromTime,
    toTime,
    isDateNotInPast,
    isStartTimeNotInPast,
    isTimeValid,
    formatRequirementsMet,
    placeId,
    isSubmitting
  ])

  return (
    <Modal autoSize onClose={onClose} customClassNames={`${cls}__wizard-body`}>
      <>
        <div className={`${cls}__wizard-title`}>
          {isEditMode ? t.details.updateMeeting : t.details.createMeeting}
        </div>

        <div className={formCls}>
          <div className={`${formCls}__section`}>
            <InputField
              name="meetingTitle"
              label={t.createMeetingForm.meetingTitle}
              value={title}
              onChange={setTitle}
            />

            <InputArea
              name="meetingDescription"
              label={t.createMeetingForm.descriptionOptional}
              initialRows={4}
              value={description}
              onChange={setDescription}
            />
          </div>

          <div className={`${formCls}__section`}>
            <InputDatePicker
              className={`${formCls}__date-picker`}
              leftIcon={false}
              label={t.createMeetingForm.date}
              name="meetingDate"
              iconRight="calendar"
              value={date}
              onChange={setDate}
            />

            <div className={`${formCls}__times`}>
              <TimePicker
                className={`${formCls}__times__input`}
                label={t.createMeetingForm.from}
                value={fromTime}
                onChange={setFromTime}
              />

              <TimePicker
                className={`${formCls}__times__input`}
                label={t.createMeetingForm.to}
                value={toTime}
                onChange={setToTime}
              />
            </div>
          </div>

          <div className={`${formCls}__section`}>
            <SelectField
              label={t.createMeetingForm.meetingFormat}
              required
              value={format}
              options={FORMAT_OPTIONS}
              onChange={(value) => {
                onFormatSelect(value)
              }}
            />

            {showOnlineMeetingUrl && (
              <InputField
                name="meetingUrl"
                label={t.createMeetingForm.onlineMeetingLink}
                autoComplete="url"
                type="url"
                value={onlineMeetingUrl}
                onChange={setOnlineMeetingUrl}
              />
            )}

            {showPhysicalLocation && (
              <>
                <InputField
                  name="meetingLocationDescription"
                  label={t.createMeetingForm.locationDescription}
                  value={locationDescription}
                  onChange={setLocationDescription}
                />

                <AddressPicker
                  autoFocusSearchInput={false}
                  label={t.createMeetingForm.location}
                  address={prefilledAddress}
                  preFilledAddress={prefilledAddress}
                  onChange={onAddressChange}
                />
              </>
            )}
          </div>
        </div>

        <div className={`${cls}__wizard-actions`}>
          <button type="button" className={`${cls}__back`} onClick={onClose}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className={`${cls}__submit`}
            disabled={!canSubmit}
            onClick={async () => {
              try {
                setIsSubmitting(true)

                const trimmedDate = date.trim()
                const trimmedFromTime = fromTime.trim()
                const trimmedToTime = toTime.trim()

                if (!trimmedDate || !trimmedFromTime || !trimmedToTime) {
                  return
                }

                if (!DateHelper.isTodayOrBefore(trimmedDate)) {
                  return
                }

                if (
                  DateHelper.isToday(trimmedDate) &&
                  !DateHelper.isAfterNow(trimmedDate, trimmedFromTime)
                ) {
                  return
                }

                const startMoment = parseDateTime(trimmedDate, trimmedFromTime)
                const endMoment = parseDateTime(trimmedDate, trimmedToTime)

                if (
                  !startMoment.isValid() ||
                  !endMoment.isValid() ||
                  !startMoment.isBefore(endMoment)
                ) {
                  return
                }

                const startTime = startMoment.toISOString()
                const endTime = endMoment.toISOString()

                const trimmedOnlineMeetingUrl = onlineMeetingUrl.trim() || undefined
                const trimmedLocationDescription =
                  locationDescription.trim() || undefined

                const shouldSendOnlineUrl =
                  format === EventFormat.Online || format === EventFormat.Hybrid
                const shouldSendPhysicalLocation =
                  format === EventFormat.InPerson || format === EventFormat.Hybrid

                if (isEditMode) {
                  const response = await updateMeeting({
                    variables: {
                      input: {
                        id: prefill!.id,
                        title: title.trim(),
                        description: description.trim() || undefined,
                        startTime,
                        endTime,
                        format,
                        locationDescription: shouldSendPhysicalLocation
                          ? trimmedLocationDescription
                          : undefined,
                        placeId: shouldSendPhysicalLocation
                          ? (placeId || undefined)
                          : undefined,
                        onlineMeetingUrl: shouldSendOnlineUrl
                          ? trimmedOnlineMeetingUrl
                          : undefined
                      }
                    }
                  })

                  const errors = response.data?.updateMeeting?.errors ?? []
                  if (!errors.length) {
                    onCreated()
                  } else {
                    alert(t.createMeetingModal.errorGeneric)
                  }
                } else {
                  const response = await createMeeting({
                    variables: {
                      input: {
                        resourceId: projectId,
                        title: title.trim(),
                        description: description.trim() || undefined,
                        startTime,
                        endTime,
                        format,
                        locationDescription: shouldSendPhysicalLocation
                          ? trimmedLocationDescription
                          : undefined,
                        placeId: shouldSendPhysicalLocation
                          ? (placeId || undefined)
                          : undefined,
                        onlineMeetingUrl: shouldSendOnlineUrl
                          ? trimmedOnlineMeetingUrl
                          : undefined
                      }
                    }
                  })

                  const errors = response.data?.createMeeting?.errors ?? []
                  if (!errors.length) {
                    onCreated()
                  } else {
                    alert(t.createMeetingModal.errorGeneric)
                  }
                }
              } catch {
                alert(t.createMeetingModal.errorGeneric)
              } finally {
                setIsSubmitting(false)
              }
            }}
          >
            {isEditMode ? t.common.update : t.common.create}
          </button>
        </div>
      </>
    </Modal>
  )
}
